/**
 * Config Service
 *
 * Fetches runtime feature flags from the backend.
 * These flags control which payment methods and features are available.
 */

import { api } from "@/lib/api";

export interface StripeFeatureFlag {
  enabled: boolean;
  publishableKey: string | null;
}

/**
 * Generic boolean feature flag (no publishable key).
 * Used for domains, databases, and future simple on/off features.
 */
export interface FeatureFlag {
  enabled: boolean;
}

/**
 * Stable keys configured in Admin → Platform Settings → Feature Flags.
 *
 * To add a new built-in flag (backend: WiCloud.Application/Common/Constants/
 * FeatureFlagRegistry.cs carries the same checklist):
 * 1. Backend — SettingType member, FeatureFlagRegistry.KnownFlags entry,
 *    IFeatureFlagService method, wired into ConfigController.GetFeatureFlags.
 * 2. Admin — add a matching entry (same key string) to FEATURE_FLAGS in
 *    wi-cloud-admin/src/types/platform-setting.ts.
 * 3. Add the key here, and a useXEnabled() hook in ./useFeatureFlags.ts that
 *    reads data.flags[CLIENT_FEATURE_FLAG_KEYS.x]?.enabled — default `false`.
 * No seed data or migration needed — an admin creates the row from the Feature
 * Flags tab, and it is disabled until they do.
 */
export const CLIENT_FEATURE_FLAG_KEYS = {
  domains: "domain_registration_enabled",
  databases: "database_management_enabled",
  zone: "zone_display_enabled",
  dns: "dns_management_enabled",
  objectStorage: "object_storage_enabled",
  firewall: "firewall_enabled",
  monitoring: "monitoring_enabled",
  eInvoice: "e_invoice_enabled",
  supportWidget: "support_widget_enabled",
  vmDeletion: "vm_deletion_enabled",
  vmSuspend: "vm_suspend_enabled",
  agreementSignup: "agreement_signup_commitment_enabled",
  agreementVmCreation: "agreement_vm_creation_commitment_enabled",
  vmUpgrade: "vm_upgrade_enabled",
  bankTransfer: "bank_transfer_enabled",
} as const;
export type ClientFeatureFlagKey =
  (typeof CLIENT_FEATURE_FLAG_KEYS)[keyof typeof CLIENT_FEATURE_FLAG_KEYS];

export interface FeatureFlags {
  stripe: StripeFeatureFlag;
  domains: FeatureFlag;
  databases: FeatureFlag;
  zone: FeatureFlag;
  dns: FeatureFlag;
  objectStorage: FeatureFlag;
  firewall: FeatureFlag;
  monitoring: FeatureFlag;
  /** Stable Admin keys. Prefer this map for all non-Stripe feature gates. */
  flags: Partial<Record<ClientFeatureFlagKey, FeatureFlag>>;
}

export const configService = {
  /**
   * Get feature flags (anonymous endpoint).
   * Controls which payment methods and features are available.
   */
  getFeatures: (): Promise<FeatureFlags> => {
    return api.get("/config/features");
  },
};
